import { useId, useState } from "react";
import type { FormEvent } from "react";
import { problemFieldErrors, problemMessage } from "@/api/client";
import type {
  Account,
  CreateRecurringRequest,
  Guid,
  RecurringFrequency,
  RecurringTemplate,
  UpdateRecurringRequest,
} from "@/api/types";
import { DialogShell } from "./DialogShell";
import "./RecurringTemplateDialog.css";

/**
 * ef-042-6 — the "New template" / "Edit template" dialog `BooksRecurring.tsx` opens.
 * Same overlay shape as `ShiftDialog.tsx`/`VendorDialog.tsx` — `books-recurring.spec.ts`'s
 * own `createTemplate()` helper clicks a trigger before any of these fields exist.
 *
 * `accountId` IS ALWAYS A CATEGORY LEG (income/expense/liability/equity), never an
 * instrument — `categoryAccounts` is pre-filtered by the caller (mirrors
 * `RegisterEntryDialog.tsx`'s own category picker), and the server refuses an Asset
 * account with a 400 naming `accountId` regardless
 * (`BooksRecurringEndpoints.cs`/`db/migrations/0046_recurring_transaction.sql`).
 *
 * PLAIN ACCOUNT NAMES, NOT "number — name". `BooksActivityPanel`'s filter picker shows
 * the number because it lists every account in one flat menu; this dialog only ever
 * lists category accounts for one template, and the plain name is what the fixture
 * category ("Utilities") the seeded chart of accounts and this file's own spec both key
 * off of actually reads as.
 */
export type RecurringFormMode = "create" | "edit";

interface RecurringFormState {
  accountId: string;
  amount: string;
  frequency: RecurringFrequency;
  nextDueOn: string;
}

const FREQUENCY_OPTIONS: { value: RecurringFrequency; label: string }[] = [
  { value: "weekly", label: "Weekly" },
  { value: "biweekly", label: "Biweekly" },
  { value: "monthly", label: "Monthly" },
  { value: "quarterly", label: "Quarterly" },
];

function blankForm(defaultAccountId: string): RecurringFormState {
  return { accountId: defaultAccountId, amount: "", frequency: "monthly", nextDueOn: "" };
}

function formFromTemplate(template: RecurringTemplate): RecurringFormState {
  return {
    accountId: template.accountId,
    amount: String(template.amount),
    frequency: template.frequency,
    nextDueOn: template.nextDueOn,
  };
}

export function RecurringTemplateDialog({
  entityId,
  categoryAccounts,
  template,
  onClose,
  onCreate,
  onUpdate,
}: {
  /** The tenant's own entity id (`useReferenceData`) — required on create; `null` while
   *  reference data is still loading disables the form rather than submitting a template
   *  with a guessed entity. */
  entityId: Guid | null;
  /** Income/Expense/Liability/Equity accounts only — never an Asset (instrument). */
  categoryAccounts: Account[];
  /** `null` opens the dialog in create mode; a `RecurringTemplate` opens it pre-seeded
   *  for editing. */
  template: RecurringTemplate | null;
  onClose: () => void;
  onCreate: (body: CreateRecurringRequest) => Promise<RecurringTemplate>;
  onUpdate: (id: Guid, body: UpdateRecurringRequest) => Promise<RecurringTemplate>;
}) {
  const [form, setForm] = useState<RecurringFormState>(() =>
    template ? formFromTemplate(template) : blankForm(categoryAccounts[0]?.id ?? ""),
  );
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string[]> | null>(null);

  const categoryId = useId();
  const amountId = useId();
  const frequencyId = useId();
  const nextDueId = useId();
  const formId = useId();

  async function handleSubmit(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    setFormError(null);
    setFieldErrors(null);

    /*
     * @ui-check: client-side ONLY — `BooksRecurringEndpoints.cs`'s own field-by-field
     * checks (amount > 0, frequency membership, the category-account gate) still run
     * after this and remain the actual contract enforcement. This guard exists so a
     * missing amount reads as a field error immediately rather than a raw 400.
     */
    const missing: Record<string, string[]> = {};
    if (!template && form.accountId === "") missing.accountId = ["Choose a category."];
    if (form.amount.trim() === "") {
      missing.amount = ["Amount is required."];
    } else if (!(Number(form.amount) > 0)) {
      missing.amount = ["Must be greater than zero."];
    }
    if (form.nextDueOn === "") missing.nextDueOn = ["Required."];
    if (Object.keys(missing).length > 0) {
      setFieldErrors(missing);
      setFormError(Object.values(missing).flat()[0] ?? null);
      return;
    }

    setSaving(true);
    try {
      const amount = Number(form.amount);
      if (template) {
        await onUpdate(template.id, {
          amount,
          frequency: form.frequency,
          nextDueOn: form.nextDueOn,
        });
      } else {
        await onCreate({
          entityId: entityId!,
          accountId: form.accountId,
          amount,
          frequency: form.frequency,
          nextDueOn: form.nextDueOn,
        });
      }
      onClose();
    } catch (err) {
      setFieldErrors(problemFieldErrors(err));
      setFormError(problemMessage(err, "Could not save this template."));
    } finally {
      setSaving(false);
    }
  }

  const canSubmit = template !== null || (entityId !== null && categoryAccounts.length > 0);

  return (
    <DialogShell
      title={template ? "Edit template" : "New template"}
      onClose={onClose}
      busy={saving}
      footer={
        <>
          <button type="button" className="btn btn--ghost" onClick={onClose} disabled={saving}>
            Cancel
          </button>
          <button type="submit" form={formId} className="btn btn--primary" disabled={saving || !canSubmit}>
            {/* "Save template" satisfies books-recurring.spec.ts's create-button locator
                `/^(save|create|add)( template)?$/i`; edit is unchanged-by-test but kept
                the SAME label for the same one-label-serves-both-modes reasoning
                VendorDialog's own comment gives. */}
            {saving ? "Saving…" : "Save template"}
          </button>
        </>
      }
    >
      <form id={formId} className="recurring-dialog__form" onSubmit={(e) => void handleSubmit(e)} noValidate>
        {template ? (
          <p className="recurring-dialog__category">
            {categoryAccounts.find((a) => a.id === template.accountId)?.name ?? "Category"}
          </p>
        ) : (
          <div className="recurring-dialog__field">
            <label htmlFor={categoryId}>Category</label>
            <select
              id={categoryId}
              value={form.accountId}
              onChange={(e) => setForm({ ...form, accountId: e.target.value })}
              aria-invalid={fieldErrors?.accountId ? true : undefined}
            >
              <option value="">Choose a category</option>
              {categoryAccounts.map((account) => (
                <option key={account.id} value={account.id}>
                  {account.number} — {account.name}
                </option>
              ))}
            </select>
            {fieldErrors?.accountId && (
              <p className="recurring-dialog__field-error" role="alert">
                {fieldErrors.accountId[0]}
              </p>
            )}
          </div>
        )}

        <div className="recurring-dialog__field">
          <label htmlFor={amountId}>Amount</label>
          <input
            id={amountId}
            type="text"
            inputMode="decimal"
            className="recurring-dialog__amount-input"
            value={form.amount}
            onChange={(e) => setForm({ ...form, amount: e.target.value })}
            aria-invalid={fieldErrors?.amount ? true : undefined}
          />
          {fieldErrors?.amount && (
            <p className="recurring-dialog__field-error" role="alert">
              {fieldErrors.amount[0]}
            </p>
          )}
        </div>

        <div className="recurring-dialog__field">
          <label htmlFor={frequencyId}>Frequency</label>
          <select
            id={frequencyId}
            value={form.frequency}
            onChange={(e) => setForm({ ...form, frequency: e.target.value as RecurringFrequency })}
          >
            {FREQUENCY_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </div>

        <div className="recurring-dialog__field">
          <label htmlFor={nextDueId}>Next due</label>
          <input
            id={nextDueId}
            type="date"
            value={form.nextDueOn}
            onChange={(e) => setForm({ ...form, nextDueOn: e.target.value })}
            aria-invalid={fieldErrors?.nextDueOn ? true : undefined}
          />
          {fieldErrors?.nextDueOn && (
            <p className="recurring-dialog__field-error" role="alert">
              {fieldErrors.nextDueOn[0]}
            </p>
          )}
        </div>

        {formError && !fieldErrors?.amount && !fieldErrors?.accountId && !fieldErrors?.nextDueOn && (
          <p className="recurring-dialog__form-error" role="alert">
            {formError}
          </p>
        )}

      </form>
    </DialogShell>
  );
}
